import type { AptosWalletAccount } from '@dynamic-labs-sdk/aptos';
import { getAptosClient, signTransaction } from '@dynamic-labs-sdk/aptos';
import type { FC, FormEvent } from 'react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

type SignTransactionDialogProps = {
  walletAccount: AptosWalletAccount;
};

const OCTAS_PER_APT = 100_000_000;

export const SignTransactionDialog: FC<SignTransactionDialogProps> = ({
  walletAccount,
}) => {
  const [isOpen, _setIsOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const [signature, setSignature] = useState<string | undefined>();

  const [recipient, setRecipient] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [asFeePayer, setAsFeePayer] = useState(false);

  const setIsOpen = (open: boolean) => {
    _setIsOpen(open);
    if (!open) {
      setRecipient('');
      setAmount('');
      setAsFeePayer(false);
      setSignature(undefined);
    }
  };

  const handleSignTransaction = async (e: FormEvent<HTMLFormElement>) => {
    setSignature(undefined);
    e.preventDefault();

    try {
      setLoading(true);

      const aptosClient = await getAptosClient({ walletAccount });

      const octasAmount = Math.floor(Number(amount) * OCTAS_PER_APT);

      const transaction = await aptosClient.transaction.build.simple({
        data: {
          function: '0x1::aptos_account::transfer',
          functionArguments: [recipient, octasAmount],
        },
        sender: walletAccount.address,
      });

      const result = await signTransaction({
        asFeePayer,
        transaction,
        walletAccount,
      });

      setSignature(result.signature.toString());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Sign Transaction
        </Button>
      </DialogTrigger>

      <DialogContent
        className="sm:max-w-[425px]"
        aria-describedby="sign-transaction-dialog"
      >
        <DialogTitle>Sign Transaction</DialogTitle>
        <form
          onSubmit={handleSignTransaction}
          className="flex flex-col space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="recipient">Recipient</Label>
            <Input
              type="text"
              name="recipient"
              placeholder="Enter recipient address"
              required
              value={recipient}
              onChange={(e) => {
                setRecipient(e.target.value);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="amount">Amount (APT)</Label>
            <Input
              type="text"
              name="amount"
              placeholder="Enter amount"
              required
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
              }}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="asFeePayer">Sign as Fee Payer</Label>
            <Switch
              id="asFeePayer"
              checked={asFeePayer}
              onCheckedChange={setAsFeePayer}
            />
          </div>
          <Button
            type="submit"
            loading={loading}
            disabled={loading || !recipient || !amount}
          >
            Sign
          </Button>

          {signature && (
            <div className="rounded-lg border border-border/50 bg-muted/30 px-3 py-2.5">
              <p className="text-xs font-medium text-muted-foreground mb-1.5">
                Signature
              </p>
              <pre className="font-mono text-xs text-muted-foreground break-all whitespace-pre-wrap">
                {signature}
              </pre>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
